/**
 * The last entitlement answer RevenueCat gave us, remembered per user.
 *
 * WHY THIS EXISTS. `SubscriptionGate` wraps every route in the app, so "not
 * entitled" does not mean "no Pro features" — it means the app will not open.
 * And entitlement was decided solely by a live `Purchases.getCustomerInfo()`,
 * with an 8-second timeout that gives up and leaves `customerInfo` as `null`.
 * A null customerInfo reads as "not a subscriber", so a slow network — or a
 * cold start with no SDK cache, after a reinstall or on a new device — showed
 * a paying customer a purchase screen and locked them out of the offline
 * scoring this app is sold on. On a golf course, which is where the signal is
 * worst and the app is most needed.
 *
 * The RC SDK keeps its own CustomerInfo cache, which covers a brief outage.
 * This is the second line: it survives the SDK having nothing to say.
 *
 * THE RULES, and why each one:
 *
 *   · KEYED BY FIREBASE UID. Two people share a phone, or one person signs out
 *     and a friend signs in — a shared key would hand over the subscription.
 *     Same reasoning as every other cached snapshot here (see CLAUDE.md on
 *     society-keyed Home caches, and the bug that taught it).
 *   · TIME-STAMPED, and honoured only inside a TTL. A snapshot is not a
 *     licence; it expires.
 *   · A LIVE ANSWER ALWAYS WINS, in both directions. This is only ever
 *     consulted when there is no live answer at all, so a lapsed subscriber
 *     keeps access only for as long as they never once reach the network — at
 *     which point they cannot sync a score either.
 *   · FAILS OPEN on a corrupt or unreadable store, never closed: every read is
 *     wrapped, and an unusable snapshot is simply "no answer", which leaves the
 *     live path exactly as it was.
 */

// Generous on purpose. The only way to hold access past a real expiry is to
// stay off the network for the whole window, and the cost of that is a few
// weeks of unpaid use by someone who cannot sync. The cost of being too mean
// is a paying customer locked out on the first tee.
export const ENTITLEMENT_TTL_MS = 30 * 24 * 60 * 60 * 1000;   // 30 days

const KEY_PREFIX = 'rc_entitlement_';

export const entitlementCacheKey = (uid) => `${KEY_PREFIX}${uid}`;

/** Remember a LIVE answer from RevenueCat. `entitled` must be a boolean. */
export const rememberEntitlement = (uid, entitled, now = Date.now()) => {
  if (!uid || typeof entitled !== 'boolean') return false;
  try {
    localStorage.setItem(entitlementCacheKey(uid),
      JSON.stringify({ entitled, at: now }));
    return true;
  } catch {
    // Private mode, quota, disabled storage — the live path is unaffected.
    return false;
  }
};

/**
 * The remembered answer for this user, or `null` when there isn't a usable
 * one — missing, malformed, or past the TTL. `null` means "no answer", which
 * is deliberately different from `false` ("RC said they are not entitled").
 */
export const readRememberedEntitlement = (uid, now = Date.now()) => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(entitlementCacheKey(uid));
    if (!raw) return null;
    const snap = JSON.parse(raw);
    if (!snap || typeof snap.entitled !== 'boolean'
        || typeof snap.at !== 'number' || !Number.isFinite(snap.at)) return null;
    // A stamp in the future is a clock change, not a fresh snapshot. Treat it
    // as unusable rather than as valid for another 30 days.
    if (snap.at > now) return null;
    if (now - snap.at > ENTITLEMENT_TTL_MS) return null;
    return snap.entitled;
  } catch {
    return null;
  }
};

export const forgetEntitlement = (uid) => {
  if (!uid) return;
  try { localStorage.removeItem(entitlementCacheKey(uid)); } catch {}
};
